/// <amd-module name="Repos/Views/PullRequest/PullRequestViewService" />
import { translateDisplayNamesToStorageKeys, translateStorageKeysToDisplayNames } from "Mention/Common/DisplayNameStorageKeyTranslationService";
import { useStorageKeysInMentions } from "Mention/Common/Util";
import {
    ISubscriptionFilter,
    NotificationSubscriptionCreateParameters,
    SubscriptionQuery,
    SubscriptionQueryCondition
} from "Notifications/Common/Contracts/Notification";
import * as React from "react";
import { IChangeWithDiscussionThread, IGitCommitChange, ILocalFileChangesCollection } from "Repos/Changes/Contracts";
import { findChange, getChangesCollection, LocalFileChangesCollection } from "Repos/Changes/Util";
import { IChangesActionsService } from "Repos/ChangesActions/Contracts";
import { getGitNavigationService, IGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { IChange, IChangeCollection, IChangesViewService } from "Repos/ChangesCommon/Contracts";
import {
    GitCommitRef,
    GitPullRequest,
    GitPullRequestCompletionOptions,
    GitPullRequestIteration,
    GitRef,
    GitRefUpdate,
    GitRepository,
    GitVersionDescriptor,
    IdentityRefWithVote,
    PullRequestAsyncStatus,
    PullRequestStatus,
    VersionControlChangeType
} from "Repos/Common/Contracts/Git";
import { ICommit } from "Repos/Common/Contracts/History";
import { PolicyEvaluationStatus } from "Repos/Common/Contracts/Policy";
import { getReposRoutingService, IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { branchNameToRef, EMPTY_OBJECT_ID, getShortCommitId, refToBranchName } from "Repos/Common/Util/Ref";
import { branchToVersionDescriptor, commitToVersionDescriptor, gitVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import {
    IChangesTreeDiscussionIntegration,
    IDiffSummaryDiscussionIntegration,
    LineInfoType,
    SpecialCommentPositions
} from "Repos/Discussion/Common/DiscussionContracts";
import { IGitFilesService } from "Repos/Files/GitFilesService";
import { getReposMessagesService, IReposMessagesService } from "Repos/Messages/ReposMessagesService";
import { tryReplacingAttachments } from "Repos/PullRequest/Attachments/Attachment";
import { getSuggestedTagsService } from "Repos/PullRequestCommon/SuggestedTagsService";
import { WorkItemNextStateOnTransition } from "Repos/Views/PullRequest/Contracts/WorkItemTracking";
import { WorkItemTransitionWarning } from "Repos/Views/PullRequest/Contracts/WorkItemTransitionWarning";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { IWorkItemTrackingRestClient } from "Repos/Views/PullRequest/RestClient/WorkItemTracking";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { WebApiTagDefinition } from "Tfs/Platform/Contracts/Core";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { IReadonlyObservableArray, IReadonlyObservableValue, IReadyableReadonlyObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { caseInsensitiveContains, EmptyGuidString, equals, format } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { IPeoplePickerProvider, PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { IEditorWidgetRegistration } from "VSS/Features/Monaco/Contracts";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { IUnsavedChangesService } from "VSS/Features/PlatformUI/UnsavedChangesService";
import { IVssPageContext, IVssService, Services, setRemoteSerializationSettings, VssService } from "VSS/Platform/Context";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { onClickFPS } from "VSS/Platform/FPS";
import { css, IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon } from "VSSUI/Icon";
import { IIdentity } from "VSSUI/IdentityPicker";
import { IMenuItem } from "VSSUI/Menu";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { IBasicIdentityRef, IPersonaProvider } from "VSSUI/Persona";
import { Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { ITreeItem } from "VSSUI/Utilities/TreeItemProvider";
import { getGutterAdornmentWidgetRegistration } from "./Components/PullRequestDetails/GutterAdornment";
import {
    IApproverCountPolicyEvaluation,
    ICompletionStatus,
    IConflictsData,
    IIteration,
    IPullRequestExtensionContext,
    IRequiredReviewersPolicyEvaluation,
    IReviewer,
    PolicyTypeId,
    PullRequestIterationChanges,
    PullRequestMessageType,
    ReviewerVote
} from "./Contracts/PullRequest";
import { IPullRequestActionsService } from "./Contracts/PullRequestActions";
import { IGutterAdornment, IGutterAdornmentBlock, IndicatorType, IPullRequestAdornmentService } from "./Contracts/PullRequestAdornments";
import { IPullRequestUpdatesService } from "./Contracts/PullRequestUpdates";
import {
    DiscussionFilterCommentedKeys,
    DiscussionFilterCommentOwnerKeys,
    DiscussionFilterCommentPreferenceKeys,
    DiscussionFilterCommentStatusKeys,
    DiscussionFilterKeys,
    getDiscussionFilterService
} from "./Discussion/PullRequestDiscussionFilterService";
import { getPullRequestDiscussionService } from "./Discussion/PullRequestDiscussionThreadService";
import {
    IInternalPullRequestDataService,
    IPullRequestAutoCompleteData,
    IPullRequestBasicData,
    ISourceRefStatus,
    PullRequestDataTypes
} from "./PullRequestDataService";
import { hasPermission, PullRequestPermissions, setPermissions } from "./PullRequestPermissionService";
import { getGitClient } from "./RestClient/Git";
import { getNotificationClient } from "./RestClient/Notification";
import { updateIgnoreList } from "./Util/Check";
import { getAutomaticReviewerPolicies, getBlockingPolicies } from "./Util/Policy";
import { createReviewerPolicyLookup, IRequiredReviewerPolicyLookup } from "./Util/Reviewer";
import { buildCheckmarksMap, calculateViewedCount, deserializeViewedState, getUpdateProperties, ViewedState } from "./Util/Viewed";
import { getVisitData, IPullRequestVisitData, updateVisitData } from "./Util/Visit";
import { getValidApprovalVotes, getVoteStatusProps, getVoteText, hasApproved } from "./Util/Vote";
import { IWorkItemEntry } from "Wit/LinkedWorkItemsDropdown/Util/Data";
import { ILinkedWorkItemsPullRequestDetailViewService } from "Wit/LinkedWorkItemsDropdown/LinkedWorkItemsPullRequestDetailViewService"

export namespace FilesFilterKeys {
    export const keyword = "keyword";
    export const iteration = "iteration";
    export const viewedFile = "viewedFile";
    export const commentedFile = "commentedFile";
}

export const enum FilesFilterViewedKeys {
    all = "file-view-all",
    pending = "file-view-pending",
    viewed = "file-view-viewed"
}

export interface IPullRequestViewService extends IChangesViewService, IVssService {
    addReviewer(identity: IIdentity, required: boolean): void;
    addTag(tag: string): void;
    cancelAutoComplete(): void;
    canDeclineReviewer(reviewerId: string): boolean;
    canFlagReviewer(reviewerId: string): boolean;
    changeTargetBranch(): void;
    cherryPickPullRequest(): void;
    /**
     * Marks as completed a pull request which changes are already merged externally. No completion panel is displayed.
     */
    closePullRequest(): void;
    commitLocalChanges(): void;
    completePullRequest(isAutoComplete: boolean): void;
    deleteSourceBranch(): void;
    getActivityFeedFilter(): string;
    getAllCommitsUrl(): IReadonlyObservableValue<string | undefined>;
    getAllChangedPaths(): IReadonlyObservableArray<string>;
    getBaseIterationId(): IReadonlyObservableValue<number>;
    getChanges(): IReadonlyObservableValue<IChangeCollection<GitVersionDescriptor>>;
    getCommits(): IReadonlyObservableArray<ICommit>;
    getCompletionStatus(): IReadonlyObservableValue<ICompletionStatus>;
    getConflictsData(): IReadonlyObservableValue<IConflictsData | undefined>;
    getCreationDate(): Date;
    getCreator(): IdentityRef;
    getDescription(): IReadonlyObservableValue<string | undefined>;
    getDescriptionEditMode(): IReadonlyObservableValue<boolean>;
    getExtensionContext(): IPullRequestExtensionContext;
    getFileUrl(iterationId: string, baseIterationId: string): string;
    getFilesFilter(): Filter;
    getId(): number;
    getIsDraft(): IReadonlyObservableValue<boolean>;
    getIsFollowed(): IReadonlyObservableValue<boolean>;
    getIsTitleDirty(): IReadonlyObservableValue<boolean>;
    getItemMenuItems(
        item: IChange,
        discussionIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>
    ): IReadonlyObservableArray<IMenuItem>;
    getIterationChanges(iterationId: number, baseId: number): Promise<IChangeCollection<GitVersionDescriptor> | undefined>;
    getIterations(): IReadonlyObservableArray<IIteration>;
    getIterationsSupport(): boolean;
    getLastMergeCommit(): GitVersionDescriptor | undefined;
    getLatestChanges(): IReadonlyObservableValue<IChangeCollection<GitVersionDescriptor>>;
    getLatestIterationId(): number;
    getLiveUpdatesEnabled(): IReadonlyObservableValue<boolean>;
    getLocalChanges(): ILocalFileChangesCollection;
    getMergeSourceCommit(): GitVersionDescriptor | undefined;
    getMergeTargetCommit(): GitVersionDescriptor | undefined;
    getPeoplePickerProvider(): IPeoplePickerProvider;
    getPersonaProvider(): IPersonaProvider;
    getPreviousVisitDate(): Date;
    getReviewers(): IReadonlyObservableArray<IReviewer>;
    getSelectedThreadId(): IReadonlyObservableValue<number | undefined>;
    getSelectedIterationId(): IReadonlyObservableValue<number>;
    getSelectedTabId(): IReadonlyObservableValue<string>;
    getSourceBranch(): string;
    getSourceDeleteBlockReason(): string | undefined;
    getSourceRepository(): GitRepository;
    getStatus(): IReadonlyObservableValue<PullRequestStatus>;
    getTags(): IReadonlyObservableArray<string>;
    getTargetBranch(): IReadonlyObservableValue<string>;
    getTargetBranchPolicyUrl(): string;
    getTargetRepository(): GitRepository;
    getTitle(): IReadonlyObservableValue<string>;
    getVersionsForIterationRange(
        iterationId: number,
        baseIterationId: number
    ): { originalVersion: GitVersionDescriptor; modifiedVersion: GitVersionDescriptor };
    getViewedCount(): IReadonlyObservableValue<number>;
    getViewWidgets(): IReadonlyObservableArray<IEditorWidgetRegistration>;
    loadAdornmentProviders(): void;
    removeReviewer(reviewerId: string): void;
    removeTag(tag: string): void;
    restartMerge(): void;
    revertPullRequest(): void;
    revertLocalChanges(): void;
    revertTitle(): void;
    saveDescription(): Promise<void>;
    saveTitle(): Promise<void>;
    selectIteration(iterationId: number, baseId: number): void;
    setActivityFeedFilter(filter: string): void;
    setChangeViewed(change: IChange, isViewed: boolean): void;
    setDraftMode(isDraft: boolean): void;
    setAutoCompleteIgnore(ignore: boolean, configurationIds: readonly number[]): void;
    setLiveUpdatesEnabled(enabled: boolean): void;
    setSelectedTabId(tabId: string): void;
    setStatus(status: PullRequestStatus): void;
    setDescription(description: string): void;
    setDescriptionEditMode(editMode: boolean, reset?: boolean): void;
    setTitle(title: string): void;
    setVote(vote: ReviewerVote, hasDeclined: boolean, isReapprove: boolean): void;
    isApprovedByCurrentUser(): boolean;
    sharePullRequest(): void;
    showChecksPanel(): void;
    startLiveUpdates(): void;
    toggleIsFollowed(): void;
    toggleReviewerFlag(reviewer: IReviewer): void;
    updateDeclineToReview(decline: boolean): void;
    updateReviewerRequired(reviewer: IReviewer, required: boolean): void;
    getWitTransitionWarningMessage(): ObservableValue<WorkItemTransitionWarning | undefined>;
    getBlockingPolicyMessage(): string;
}

const maxCommits = 99;

class PullRequestViewService extends VssService implements IPullRequestViewService {
    private activityFeedFilter = "";
    private adornmentServices: IPullRequestAdornmentService[] = [];
    private allCommitsUrl = new ObservableValue<string | undefined>(undefined);
    private allChangedPaths = new ObservableArray<string>();
    private autoCompleteBlockingPolicyIds: number[] = [];
    private baseIterationId: ObservableValue<number>;
    private changes: ObservableValue<IChangeCollection<GitVersionDescriptor>>;
    private changesCurrentKey: string = "*pending*"; // Any value to fail first comparison with new key
    private commits = new ObservableArray<ICommit>();
    private completionStatus: ObservableValue<ICompletionStatus>;
    private conflictsData = new ObservableValue<IConflictsData | undefined>(undefined);
    private currentUserId: string;
    private dataService: IInternalPullRequestDataService;
    private description = new ObservableValue<string>("");
    private descriptionEditMode = new ObservableValue<boolean>(false);
    private filesFilter = new Filter();
    private hostName: string;
    private isDraft = new ObservableValue<boolean>(false);
    private isFollowed = new ObservableValue<boolean>(false);
    private isSourceDeleted: boolean;
    private isTargetDeleted: boolean;
    private isTitleDirty = new ObservableValue(false);
    private iterationChangesCache: { [key: string]: PullRequestIterationChanges } = {};
    private iterationDiffCache: { [key: string]: PullRequestIterationChanges } = {};
    private iterations = new ObservableArray<IIteration>();
    private latestIterationId: number;
    private latestChanges: ObservableValue<IChangeCollection<GitVersionDescriptor>>;
    private liveUpdatesEnabled = new ObservableValue(true);
    private localChanges: ILocalFileChangesCollection;
    private selectedThreadId: ObservableValue<number | undefined>;
    private noIterationsSourceCommitId: string;
    private noIterationsTargetCommitId: string;
    private peoplePickerProvider: PeoplePickerProvider;
    private personaProvider: PersonaDetailsProvider;
    private pullRequest: GitPullRequest;
    private reviewerIds: Set<string>;
    private reviewers = new ObservableArray<IReviewer>();
    private reviewerPolicyLookup = new ObservableValue<IRequiredReviewerPolicyLookup>({});
    private selectedIterationId: ObservableValue<number>;
    private selectedTabId: ObservableValue<string>;
    private sourceBranch: string;
    private sourceRef: GitRef | undefined;
    private sourceRefStatus: ISourceRefStatus | undefined;
    private sourceRepository: GitRepository;
    private status = new ObservableValue<PullRequestStatus>(PullRequestStatus.Active);
    private subscriptionId: string;
    private tagRefs: WebApiTagDefinition[] = [];
    private tags = new ObservableArray<string>();
    private targetBranch = new ObservableValue<string>("");
    private targetRepository: GitRepository;
    private completingTimer = new TimerManagement();
    private title = new ObservableValue<string>("");
    private viewedCount = new ObservableValue(0);
    private viewedState: ViewedState;
    private viewWidgets = new ObservableArray<IEditorWidgetRegistration>();
    private visit: IPullRequestVisitData;
    private relatedWitTransitionItems = new ObservableArray<WorkItemNextStateOnTransition>([]);
    private transitionWarningMessage = new ObservableValue<WorkItemTransitionWarning|undefined>(undefined);
    private linkedWitItems: IReadyableReadonlyObservableArray<IWorkItemEntry>;
    private witRestTrackingClient: IWorkItemTrackingRestClient;
    private TRANSITION_ERROR_LINKS: Record<string, string>= {
        "VS1640134": "https://go.microsoft.com/fwlink/?linkid=854123",
        "VS1640135": "https://go.microsoft.com/fwlink/?linkid=858200",
    }
    private isSubscribedToLinkedWitItems = false;

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);
        this.selectedTabId.unsubscribe(this.checkFullscreenValidity);
        this.isSubscribedToLinkedWitItems && this.linkedWitItems.unsubscribe(this.updateTransitionItems.bind(this));
    };

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData()!;
        this.currentUserId = pageData.user.id;
        this.hostName = pageData.hostName;

        const pageStateHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const historyState = pageStateHistoryService.getProjectedState();

        this.selectedTabId = new ObservableValue(historyState["_a"] || "overview");
        this.baseIterationId = new ObservableValue(parseInt(historyState["base"]) || 0);
        this.selectedIterationId = new ObservableValue(parseInt(historyState["iteration"]) || 0);

        this.peoplePickerProvider = new PeoplePickerProvider(this.pageContext);
        this.personaProvider = new PersonaDetailsProvider(this.pageContext);

        // Subscribe to Policy data changes
        this.dataService = pageContext.getService<IInternalPullRequestDataService>("IInternalPullRequestDataService");
        this.dataService.getPolicies().subscribe(() => this.updateReviewersAndPolicies());

        this.selectedThreadId = new ObservableValue(parseInt(historyState["discussionId"]) || undefined);

        // Process Visit data
        this.visit = getVisitData(pageContext)!;
        this.viewedState = deserializeViewedState(this.visit && this.visit.viewedState);

        // Process PullRequest data
        this.dataService.getPullRequestBasicData().subscribe(this.updatePullRequestAndRefs);
        this.updatePullRequestAndRefs(this.dataService.getPullRequestBasicData().value);

        // Process auto-complete data
        this.dataService.getAutoCompleteData().subscribe(this.updateAutoCompleteData);
        this.updateAutoCompleteData(this.dataService.getAutoCompleteData().value);

        const iterations = this.dataService.getIterations();
        iterations.subscribe(() => this.updateIterations(iterations.value));
        this.updateIterations(iterations.value);

        const tags = this.dataService.getLabels();
        tags.subscribe(() => this.updateTags(tags.value));
        this.updateTags(tags.value);
        getSuggestedTagsService(this.pageContext).setUsedTags(this.tags);

        this.filesFilter.setFilterItemState(FilesFilterKeys.viewedFile, { value: [FilesFilterViewedKeys.all] });
        this.filesFilter.setFilterItemState(FilesFilterKeys.commentedFile, { value: [DiscussionFilterCommentedKeys.all] });
        this.filesFilter.setFilterItemState(DiscussionFilterKeys.commentPreference, { value: [DiscussionFilterCommentPreferenceKeys.show] });
        this.filesFilter.setFilterItemState(DiscussionFilterKeys.commentStatus, {
            value: [DiscussionFilterCommentStatusKeys.active, DiscussionFilterCommentStatusKeys.pending]
        });
        this.filesFilter.setFilterItemState(DiscussionFilterKeys.commentOwner, { value: [DiscussionFilterCommentOwnerKeys.all] });
        this.filesFilter.setDefaultState(this.filesFilter.getState());

        // Initialize discussion filter service
        getDiscussionFilterService(pageContext).setFilterState(this.filesFilter.getState(), this.selectedThreadId.value);

        // Process changes after we've processed iterations
        const changes = this.dataService.getChanges();
        const isMultiple = this.dataService.getMergeBasesStatus();
        changes.subscribe(this.updateChanges);
        this.updateChanges(changes.value);

        const latestChanges = this.dataService.getLatestChanges();
        latestChanges.subscribe(this.updateLatestChanges);
        this.updateLatestChanges(latestChanges.value);

        this.updateIterationFilterText();
        this.filesFilter.subscribe(this.applyFilesFilter);

        // Lookup whether or not this PR is being followed
        if (userHasClaim(this.pageContext, UserClaims.Member)) {
            getNotificationClient(this.pageContext)
                .querySubscriptions({
                    conditions: [
                        {
                            filter: {
                                artifactId: this.getNotificationArtifactId(),
                                artifactType: "PullRequestId",
                                eventType: null as any,
                                type: "Artifact"
                            } as ISubscriptionFilter
                        } as SubscriptionQueryCondition
                    ]
                } as SubscriptionQuery)
                .then(subscriptions => {
                    if (subscriptions && subscriptions.length) {
                        this.subscriptionId = subscriptions[0].id;
                        this.isFollowed.value = !!this.subscriptionId;
                    }
                });
        }

        let tabChangeHistoryBehavior = HistoryBehavior.newEntry;
        pageStateHistoryService.registerObservable<string>(
            this.selectedTabId,
            "_a",
            () => tabChangeHistoryBehavior,
            selectedTabId => (selectedTabId === "overview" ? "" : selectedTabId),
            queryParam => queryParam || "overview"
        );

        if (this.selectedTabId.value !== "overview" && historyState["contributionId"]) {
            // Tab contributions to the old PR details page used a "contributionId" query parameter
            // for the full id of the contributed tab. Replace the _a parameter with that if provided.
            tabChangeHistoryBehavior = HistoryBehavior.replace;
            this.selectedTabId.value = historyState["contributionId"];
            pageStateHistoryService.queueValueChange("contributionId", "", HistoryBehavior.replace);
            tabChangeHistoryBehavior = HistoryBehavior.newEntry;
        }

        pageStateHistoryService.registerObservable<number>(
            this.selectedIterationId,
            "iteration",
            HistoryBehavior.newEntry,
            id => (id === this.latestIterationId && this.baseIterationId.value === 0 ? "" : id.toFixed()),
            queryParam => parseInt(queryParam) || this.latestIterationId
        );

        pageStateHistoryService.registerObservable<number>(
            this.baseIterationId,
            "base",
            HistoryBehavior.newEntry,
            id => (id ? id.toFixed() : ""),
            queryParam => parseInt(queryParam) || 0
        );

        const navigationService: IGitNavigationService = getGitNavigationService(this.pageContext);
        const selectedItem: IReadonlyObservableValue<IChange> = navigationService.getSelectedItem();

        selectedItem.subscribe((value: IChange): void => {
            this.loadAdornments();

            const change: IChangeWithDiscussionThread = value as IChangeWithDiscussionThread;
            if (change.thread) {
                if (this.selectedThreadId.value !== change.thread.id) {
                    this.selectedThreadId.value = change.thread.id;
                }
            } else {
                if (this.selectedThreadId.value) {
                    this.selectedThreadId.value = undefined;
                }
            }
        });

        pageStateHistoryService.registerObservable<IChange>(
            selectedItem,
            "path",
            HistoryBehavior.newEntry,
            change => (change && change.path !== "/" ? change.path : ""),
            queryParam => findChange(this.changes.value, queryParam) || selectedItem.value || this.changes.value.rootTreeItem
        );

        pageStateHistoryService.registerObservable<number | undefined>(
            this.selectedThreadId,
            "discussionId",
            HistoryBehavior.newEntry,
            id => (id ? id.toFixed() : ""),
            queryParam => parseInt(queryParam) || undefined
        );

        let unsavedItems: string[] = [];
        this.pageContext.getService<IUnsavedChangesService>("IUnsavedChangesService").register({
            isDirty: () => {
                let dirty = false;
                unsavedItems = [];

                if (this.isTitleDirty.value) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedTitleText);
                }

                if ((this.description.value || "") !== (this.pullRequest.description || "")) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedDescriptionText);
                }

                if (getPullRequestDiscussionService(this.pageContext).hasUnsavedThreads()) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedCommentsText);
                }

                if (this.localChanges.isDirty().value) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedCodeChanges);
                }

                return dirty;
            },
            getDirtyMessage: (): string => format(Resources.UnsavedItemsMessage, unsavedItems.join(", "))
        });

        if(isMultiple) {
            const reposRoutingService = getReposRoutingService(pageContext);

            const lastMergeCommit =  this.pullRequest.lastMergeCommit && this.pullRequest.lastMergeCommit.commitId;
            const mergeTargetCommit = this.pullRequest.lastMergeTargetCommit && this.pullRequest.lastMergeTargetCommit.commitId;
            const repositoryName = this.pullRequest.repository && this.pullRequest.repository.name;
            const projectName = this.pullRequest.repository && this.pullRequest.repository.project && this.pullRequest.repository.project.name;

            const viewMergeChangesUrl =
                projectName &&
                repositoryName &&
                lastMergeCommit &&
                mergeTargetCommit &&
                reposRoutingService.getBranchCompareUrl(repositoryName, projectName, commitToVersionDescriptor(mergeTargetCommit),  commitToVersionDescriptor(lastMergeCommit));

            getReposMessagesService(this.pageContext)?.showMessage(
            {
                    buttons: [{
                        text: Resources.MultipleMergeBasesButtonText,
                        href: Resources.MultipleMergeBasesButtonURI
                    }],
                    renderMessage: () => (
                        <>
                            {
                            viewMergeChangesUrl ? // if viewMergeChangesUrl is null then lastMergeCommit haven't calculated yet and we show message about it
                                <FormatComponent elementType="span" format={Resources.MultipleMergeBasesWarning}>
                                    <span>
                                        <FPSLink href={viewMergeChangesUrl}>{Resources.ViewMergeChanges}</FPSLink>
                                    </span>
                                </FormatComponent>
                                : <span>{Resources.MultipleMergeBasesWarningNotCalculated}</span>
                            }
                        </>
                    ),
                    severity: MessageCardSeverity.Warning,
            },
            false);
        }

        this.selectedTabId.subscribe(this.checkFullscreenValidity);


        // Work item transition warning
        if (isFeatureFlagEnabled(this.pageContext, "SourceControl.GitPullRequests.WorkItemTransitionErrorMessage", false)) {
            const witLinkedService = this.pageContext.getService<ILinkedWorkItemsPullRequestDetailViewService>("ILinkedWorkItemsPullRequestDetailViewService");
            this.witRestTrackingClient = this.pageContext.getRestClient<IWorkItemTrackingRestClient>("Repos.PRDetails.WitTracking.IWorkItemTrackingRestClient");

            this.linkedWitItems = witLinkedService.getLinkedWorkItems(this.targetRepository.project.id, this.targetRepository.id, this.pullRequest.pullRequestId);
            this.linkedWitItems.subscribe(this.updateTransitionItems.bind(this));
            this.isSubscribedToLinkedWitItems = true;
        }
    }

    public addReviewer(identity: IIdentity, required: boolean) {
        if (!identity.localId || !this.reviewerIds.has(identity.localId)) {
            this.sendCreateReviewerRequest(identity, required)
                .then(result => {
                    this.reviewerIds.add(result.id);
                    const voteMap = this.createVotedByForMap(...this.reviewers.value.map(x => x.baseReviewer), result);
                    this.reviewers.push(this.convertToIReviewer(result, voteMap));
                })
                .catch(error => {
                    const errorMessage = error.serverError ? error.serverError.message : error.message;

                    const messagesService = getReposMessagesService(this.pageContext);
                    messagesService.showMessage(
                        {
                            buttons: [{ id: "try-again", onClick: () => this.addReviewer(identity, required), text: Resources.TryAgain }],
                            renderMessage: () => <span>{errorMessage}</span>,
                            severity: MessageCardSeverity.Error,
                            type: PullRequestMessageType.reviewersError
                        },
                        false
                    );
                });
        }
    }

    public addTag(tag: string) {
        getGitClient(this.pageContext)
            .createPullRequestLabel({ name: tag }, this.targetRepository.id, this.pullRequest.pullRequestId)
            .then(
                result => {
                    this.tagRefs.push(result);
                    this.tags.push(result.name);
                    getSuggestedTagsService(this.pageContext).addTagToCache(tag);
                },
                error => {
                    const messagesService = this.pageContext.getService<IReposMessagesService>("IReposMessagesService");
                    messagesService.showMessage({
                        renderMessage: () => format(Resources.TagCreateError, error.message),
                        severity: MessageCardSeverity.Error
                    });
                }
            );
    }

    public async cancelAutoComplete(): Promise<void> {
        return this.dataService.gitClientUpdatePullRequest({ autoCompleteSetBy: { id: EmptyGuidString } }, PullRequestDataTypes.autoComplete);
    }

    public canDeclineReviewer(reviewerId: string): boolean {
        const reviewer = this.reviewers.value.find(r => r.identity.id === reviewerId);
        const hasVoted = reviewer && reviewer.vote !== 0;
        const isRequired = reviewer && reviewer.baseReviewer.isRequired;
        return (
            this.status.value === PullRequestStatus.Active &&
            reviewerId === this.currentUserId &&
            reviewerId !== this.pullRequest.createdBy.id &&
            !hasVoted &&
            !isRequired &&
            isFeatureFlagEnabled(this.pageContext, "SourceControl.GitPullRequests.ReviewerFlags", false)
        );
    }

    public canFlagReviewer(reviewerId: string): boolean {
        return (
            this.status.value === PullRequestStatus.Active &&
            reviewerId === this.currentUserId &&
            reviewerId !== this.pullRequest.createdBy.id &&
            isFeatureFlagEnabled(this.pageContext, "SourceControl.GitPullRequests.ReviewerFlags", false)
        );
    }

    public changeTargetBranch(): void {
        this.getPullRequestActionsService().then(actionsService => {
            actionsService.changeTargetBranch({
                changeTargetBranch: branchName =>
                    this.dataService.gitClientUpdatePullRequest(
                        { targetRefName: branchNameToRef(branchName) },
                        PullRequestDataTypes.default | PullRequestDataTypes.changes
                    ),
                originalTargetBranchName: this.targetBranch.value,
                repository: this.targetRepository,
                sourceBranchName: this.sourceRepository.id === this.targetRepository.id ? this.sourceBranch : undefined
            });
        });
    }

    public cherryPickPullRequest(): void {
        this.getChangesActionsService().then(actionsService =>
            actionsService.cherryPickPullRequest(this.pullRequest, this.targetRepository, this.sourceBranch, this.targetBranch.value)
        );
    }

    public closePullRequest(): void {
        this.dataService.gitClientUpdatePullRequest(
            {
                completionOptions: {} as GitPullRequestCompletionOptions,
                lastMergeSourceCommit: this.pullRequest.lastMergeSourceCommit,
                status: PullRequestStatus.Completed
            },
            PullRequestDataTypes.autoComplete
        );
    }

    public commitLocalChanges(): void {
        this.getPullRequestActionsService().then(prActionsService => prActionsService.commitLocalChanges());
    }

    public completePullRequest(isAutoComplete: boolean): void {
        const service = this.pageContext.getService<IUnsavedChangesService>("IUnsavedChangesService");
        let unsavedItems: string[] = [];
        const document = {
            isDirty: () => {
                let dirty = false;
                unsavedItems = [];

                if (this.isTitleDirty.value) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedTitleText);
                }

                if ((this.description.value || "") !== (this.pullRequest.description || "")) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedDescriptionText);
                }

                if (getPullRequestDiscussionService(this.pageContext).hasUnsavedThreads()) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedCommentsText);
                }

                if (this.localChanges.isDirty().value) {
                    dirty = true;
                    unsavedItems.push(Resources.UnsavedCodeChanges);
                }
                return dirty;
            },
            getDirtyMessage: (): string => format(Resources.UnsavedItemsMessage, unsavedItems.join(", ")),
            getDirtyTitle: (): string => Resources.CannotChangePullRequestStatus,
            getDirtyOnStayBtn: (): string => Resources.Cancel,
            getDirtyOnLeaveBtn: (): string => Resources.Save
        };
        service.registerFirst(document);

        service.triggerNavigateAway(async () => {
            if (this.isTitleDirty.value) {
                await this.saveTitle();
            }

            if ((this.description.value || "") !== (this.pullRequest.description || "")) {
                await this.saveDescription();
            }
            if (getPullRequestDiscussionService(this.pageContext).hasUnsavedThreads()) {
                await getPullRequestDiscussionService(this.pageContext).saveThreads();
            }

            if (this.localChanges.isDirty().value) {
                this.commitLocalChanges();
            }

            this.getPullRequestActionsService().then(actionsService => {
                actionsService.showCompletePanel({
                    isAutoComplete,
                    onComplete: async completionOptions => {
                        const maxDescriptionLength = 3000; // same as old PR page code
                        const truncatedDescription =
                            completionOptions.mergeCommitDescription && completionOptions.mergeCommitDescription.length > maxDescriptionLength
                                ? `${completionOptions.mergeCommitDescription.substring(0, maxDescriptionLength)}...`
                                : completionOptions.mergeCommitDescription;

                        const prToSave = {
                            completionOptions: {
                                autoCompleteIgnoreConfigIds: completionOptions.autoCompleteIgnoreConfigIds,
                                bypassPolicy: !!completionOptions.policyOverrideMessage,
                                bypassReason: completionOptions.policyOverrideMessage,
                                deleteSourceBranch: completionOptions.deleteBranch,
                                mergeCommitMessage: completionOptions.mergeCommitDescription
                                    ? `${completionOptions.mergeCommitTitle}\n\n${truncatedDescription}`
                                    : completionOptions.mergeCommitTitle,
                                mergeStrategy: completionOptions.mergeStrategy,
                                transitionWorkItems: completionOptions.completeWorkItems
                            }
                        } as GitPullRequest;

                        if (isAutoComplete) {
                            prToSave.autoCompleteSetBy = { id: this.currentUserId } as IdentityRef;
                        } else {
                            prToSave.status = PullRequestStatus.Completed;
                            prToSave.lastMergeSourceCommit = this.pullRequest.lastMergeSourceCommit;
                        }

                        this.dataService.gitClientUpdatePullRequest(prToSave, PullRequestDataTypes.autoComplete);
                    },
                    sourceBranchName: this.sourceBranch
                });
            });
        });
        service.unregister(document);
    }

    public deleteSourceBranch(): void {
        if (this.sourceRef) {
            const refToDelete = {
                name: this.sourceRef.name,
                newObjectId: EMPTY_OBJECT_ID,
                oldObjectId: this.sourceRef.objectId
            } as GitRefUpdate;

            getGitClient(this.pageContext)
                .updateRefs([refToDelete], this.sourceRepository.id, this.sourceRepository.project.id)
                .then(() => {
                    this.dataService.refreshPullRequestData(PullRequestDataTypes.basicDetails);
                });
        }
    }

    public getActivityFeedFilter(): string {
        return this.activityFeedFilter;
    }

    public getAllCommitsUrl(): IReadonlyObservableValue<string | undefined> {
        return this.allCommitsUrl;
    }

    public getAllChangedPaths(): IReadonlyObservableArray<string> {
        return this.allChangedPaths;
    }

    public getBaseIterationId(): IReadonlyObservableValue<number> {
        return this.baseIterationId;
    }

    public getChanges(): IReadonlyObservableValue<IChangeCollection<GitVersionDescriptor>> {
        return this.changes;
    }

    public getCommits(): IReadonlyObservableArray<ICommit> {
        return this.commits;
    }

    public getCompareHeaderMenuItems(
        change: IChange,
        rootDiffIntegration?: IDiffSummaryDiscussionIntegration,
        smallScreen?: boolean
    ): IHeaderCommandBarItem[] {
        if (smallScreen) {
            return [];
        }
        return [
            {
                ariaLabel: Resources.ToggleFullScreenMode,
                iconProps: {
                    render: (className?: string) => {
                        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                        return (
                            <Observer fullScreenMode={layoutManager.getFullScreenMode()}>
                                {(props: { fullScreenMode: boolean }) => (
                                    <Icon className={css(className, "medium")} iconName={props.fullScreenMode ? "BackToWindow" : "FullScreen"} />
                                )}
                            </Observer>
                        );
                    }
                },
                id: "fullscreen",
                onActivate: () => {
                    const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.setFullScreenMode(!layoutManager.getFullScreenMode().value);

                    // Refresh Monaco (that won't detect the layout change). This is the same logic we have in VSSUI Splitter.
                    // We have to wait for all components to adapt to the new layout, so we need delay triggering the event.
                    requestAnimationFrame(() => {
                        const event = document.createEvent("Event");
                        event.initEvent("resize", false, true);
                        window.dispatchEvent(event);
                    });
                },
                subtle: true,
                tooltipProps: { text: Resources.ToggleFullScreenMode }
            }
        ];
    }

    public getCompletionStatus(): IReadonlyObservableValue<ICompletionStatus> {
        return this.completionStatus;
    }

    public getConflictsData(): IReadonlyObservableValue<IConflictsData | undefined> {
        return this.conflictsData;
    }

    public getCreationDate(): Date {
        return this.pullRequest.creationDate;
    }

    public getCreator(): IdentityRef {
        return this.pullRequest.createdBy;
    }

    public getDescription(): IReadonlyObservableValue<string | undefined> {
        return this.description;
    }

    public getDescriptionEditMode(): IReadonlyObservableValue<boolean> {
        return this.descriptionEditMode;
    }

    public getExtensionContext(): IPullRequestExtensionContext {
        const pullRequest = this.pullRequest;
        const context: IPullRequestExtensionContext = {
            pullRequest: {
                ...pullRequest,
                _pullRequest: pullRequest,
                lastMergeCommitId: pullRequest.lastMergeCommit && pullRequest.lastMergeCommit.commitId,
                lastMergeSourceCommitId: pullRequest.lastMergeSourceCommit && pullRequest.lastMergeSourceCommit.commitId,
                lastMergeTargetCommitId: pullRequest.lastMergeTargetCommit && pullRequest.lastMergeTargetCommit.commitId,
                projectGuid: pullRequest.repository.project.id,
                repositoryId: pullRequest.repository.id,
                repositoryName: pullRequest.repository.name,
                sourceRefName: pullRequest.sourceRefName,
                sourceFriendlyName: refToBranchName(pullRequest.sourceRefName),
                targetRefName: pullRequest.targetRefName,
                targetFriendlyName: refToBranchName(pullRequest.targetRefName)
            },
            pullRequestPageVersion: 2
        };
        setRemoteSerializationSettings(context, { serializeUnderscoreProperties: true });
        return context;
    }

    public getFilesFilter(): Filter {
        return this.filesFilter;
    }

    public getFileUrl(iterationId: string, baseIterationId: string): string {
        return getReposRoutingService(this.pageContext).getPullRequestUrl(
            this.targetRepository.name,
            this.targetRepository.project.name,
            this.getId(),
            "files",
            undefined,
            iterationId,
            baseIterationId
        );
    }

    public getId(): number {
        return this.pullRequest.pullRequestId;
    }

    public getIsDraft(): IReadonlyObservableValue<boolean> {
        return this.isDraft;
    }

    public getIsFollowed(): IReadonlyObservableValue<boolean> {
        return this.isFollowed;
    }

    public getIsTitleDirty(): IReadonlyObservableValue<boolean> {
        return this.isTitleDirty;
    }

    public getItemMenuItems(
        item: IChange,
        discussionIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>
    ): IReadonlyObservableArray<IMenuItem> {
        const { pageContext } = this;
        const menuItems: IMenuItem[] = [];

        const routingService: IReposRoutingService = pageContext.getService<IReposRoutingService>("IReposRoutingService");
        const versionDescriptor =
            !this.isSourceDeleted && this.pullRequest.lastMergeSourceCommit && item.commitId === this.pullRequest.lastMergeSourceCommit.commitId
                ? branchToVersionDescriptor(this.sourceBranch)
                : commitToVersionDescriptor(item.commitId);

        const fileUrl = routingService.getFilesUrl(this.sourceRepository.name, this.sourceRepository.project.name, item.path, versionDescriptor);

        menuItems.push({
            groupKey: "view-items",
            iconProps: { iconName: "FileCode" },
            href: fileUrl,
            id: "view-in-exporler",
            onActivate: (menuItem: IMenuItem, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                return onClickFPS(pageContext, menuItem.href!, true, event);
            },
            text: Resources.FileHub_ViewInFileExplorer
        });

        if (!item.isFolder) {
            const historyUrl = routingService.getFilesHistoryUrl(
                this.sourceRepository.name,
                this.sourceRepository.project.name,
                item.path,
                commitToVersionDescriptor(item.commitId)
            );

            const compareUrl = routingService.getFilesCompareUrl(
                this.sourceRepository.name,
                this.sourceRepository.project.name,
                item.path,
                commitToVersionDescriptor(item.commitId),
                gitVersionStringToVersionDescriptor("PGC" + item.commitId),
                commitToVersionDescriptor(item.commitId)
            );

            menuItems.push({
                groupKey: "view-items",
                href: historyUrl,
                iconProps: { iconName: "History" },
                id: "view-history",
                onActivate: (menuItem: IMenuItem, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    return onClickFPS(pageContext, menuItem.href!, true, event);
                },
                text: Resources.FileHub_ViewHistory
            });

            menuItems.push({
                groupKey: "view-items",
                href: compareUrl,
                iconProps: { iconName: "BranchCompare" },
                id: "view-compare",
                onActivate: (menuItem: IMenuItem, event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                    return onClickFPS(pageContext, menuItem.href!, true, event);
                },
                text: Resources.FileHub_ComparePreviousVersion
            });

            menuItems.push({
                groupKey: "file-actions",
                iconProps: { iconName: "Download" },
                id: "file-download",
                onActivate: () => {
                    const gitFilesService = pageContext.getService<IGitFilesService>("IGitFilesService");
                    const downloadUrl = gitFilesService.getFileDownloadUrl(item.path, commitToVersionDescriptor(item.commitId), true);

                    window.open(downloadUrl, "_blank", "noopener");
                },
                text: Resources.FileHub_Download
            });

            if (hasPermission(pageContext, PullRequestPermissions.AddEditComment) && discussionIntegration) {
                menuItems.push({
                    groupKey: "file-items",
                    iconProps: { iconName: "CommentAdd" },
                    id: "add-comment",
                    onActivate: () => {
                        const navigationService: IGitNavigationService = getGitNavigationService(this.pageContext);
                        navigationService.setSelectedItem(item);

                        if (discussionIntegration) {
                            discussionIntegration.renderAddCommentWidget(
                                item.path,
                                {
                                    lineStart: SpecialCommentPositions.File,
                                    lineEnd: SpecialCommentPositions.File,
                                    lineEndOffset: 0,
                                    lineStartOffset: 0,
                                    type: LineInfoType.Actual
                                },
                                item.changeType === VersionControlChangeType.Delete
                            );
                        }
                    },
                    text: Resources.FileHub_AddComment
                });
            }
        }

        const isReviewedItem = item.checkmarked ? item.checkmarked.value : false;
        menuItems.push({
            groupKey: "file-items",
            iconProps: { iconName: isReviewedItem ? "Undo" : "CheckMark" },
            id: "mark-as-reviewed",
            onActivate: () => {
                this.setChangeViewed(item, !isReviewedItem);
            },
            text: isReviewedItem ? Resources.FileHub_MarkAsUnreviewed : Resources.FileHub_MarkAsReviewed
        });

        return new ObservableArray(menuItems);
    }

    public async getIterationChanges(iterationId: number, baseId: number): Promise<IChangeCollection<GitVersionDescriptor> | undefined> {
        const key = `${iterationId}-${baseId}`;
        const changes = this.iterationDiffCache[key] || (await this.dataService.getIterationChanges(iterationId, baseId));

        this.iterationDiffCache[key] = changes;
        return this.processChanges(changes, "");
    }

    public getIterations(): IReadonlyObservableArray<IIteration> {
        return this.iterations;
    }

    public getIterationsSupport(): boolean {
        return !!this.pullRequest.supportsIterations;
    }

    public getLastMergeCommit(): GitVersionDescriptor | undefined {
        return this.pullRequest.lastMergeCommit && commitToVersionDescriptor(this.pullRequest.lastMergeCommit.commitId);
    }

    public getLatestChanges(): IReadonlyObservableValue<IChangeCollection<GitVersionDescriptor>> {
        return this.latestChanges;
    }

    public getLatestIterationId(): number {
        return this.latestIterationId;
    }

    public getLiveUpdatesEnabled(): IReadonlyObservableValue<boolean> {
        return this.liveUpdatesEnabled;
    }

    public getLocalChanges(): ILocalFileChangesCollection {
        return this.localChanges;
    }

    public getMergeSourceCommit(): GitVersionDescriptor | undefined {
        return this.pullRequest.lastMergeSourceCommit ? commitToVersionDescriptor(this.pullRequest.lastMergeSourceCommit.commitId) : undefined;
    }

    public getMergeTargetCommit(): GitVersionDescriptor | undefined {
        return this.pullRequest.lastMergeTargetCommit ? commitToVersionDescriptor(this.pullRequest.lastMergeTargetCommit.commitId) : undefined;
    }

    public getPeoplePickerProvider(): IPeoplePickerProvider {
        return this.peoplePickerProvider;
    }

    public getPersonaProvider(): IPersonaProvider {
        return this.personaProvider;
    }

    public getPreviousVisitDate(): Date {
        if (!this.visit.previousLastVisitedDate) {
            this.visit.previousLastVisitedDate = new Date(Date.now());
        }
        return this.visit.previousLastVisitedDate;
    }

    public getViewedCount(): IReadonlyObservableValue<number> {
        return this.viewedCount;
    }

    public getReviewers(): IReadonlyObservableArray<IReviewer> {
        return this.reviewers;
    }

    public getSelectedThreadId(): IReadonlyObservableValue<number | undefined> {
        return this.selectedThreadId;
    }

    public getSelectedIterationId(): IReadonlyObservableValue<number> {
        return this.selectedIterationId;
    }

    public getSelectedTabId(): IReadonlyObservableValue<string> {
        return this.selectedTabId;
    }

    public getSourceBranch(): string {
        return this.sourceBranch;
    }

    public getSourceDeleteBlockReason(): string | undefined {
        let cannotDeleteSourceMessage: string | undefined;

        // Determine if deleting the source branch is allowed
        if (!this.sourceRefStatus) {
            cannotDeleteSourceMessage = Resources.SourceRepoDoesNotExist;
        } else if (this.sourceRefStatus.isDefault) {
            cannotDeleteSourceMessage = Resources.CannotDeleteDefaultBranch;
        } else if (this.sourceRefStatus.isDeleted) {
            cannotDeleteSourceMessage = format(Resources.BranchHasBeenDeleted, branchToVersionDescriptor(this.getSourceBranch()));
        } else if (this.sourceRefStatus.hasPolicies) {
            cannotDeleteSourceMessage = Resources.CannotDeleteBranchWithPolicies;
        } else if (!hasPermission(this.pageContext, PullRequestPermissions.DeleteSourceBranch)) {
            cannotDeleteSourceMessage = Resources.CannotDeleteBranchNoPermission;
        }

        return cannotDeleteSourceMessage;
    }

    public getSourceRepository(): GitRepository {
        return this.sourceRepository;
    }

    public getStatus(): IReadonlyObservableValue<PullRequestStatus> {
        return this.status;
    }

    public getTags(): IReadonlyObservableArray<string> {
        return this.tags;
    }

    public getTargetBranch(): IReadonlyObservableValue<string> {
        return this.targetBranch;
    }

    public getTargetBranchPolicyUrl(): string {
        return getReposRoutingService(this.pageContext).getBranchPolicyUrl(
            this.targetRepository.name,
            this.targetRepository.project.name,
            this.pullRequest.targetRefName,
            this.targetRepository.id
        );
    }

    public getTargetRepository(): GitRepository {
        return this.targetRepository;
    }

    public getTitle(): IReadonlyObservableValue<string> {
        return this.title;
    }

    public getVersionsForIterationRange(
        iterationId: number,
        baseIterationId: number
    ): { originalVersion: GitVersionDescriptor; modifiedVersion: GitVersionDescriptor } {
        let originalCommitId = this.noIterationsTargetCommitId;
        let modifiedCommitId = this.noIterationsSourceCommitId;

        if (iterationId >= 0) {
            const currentIteration = this.iterations.value.find(it => it.id === iterationId);
            if (currentIteration) {
                modifiedCommitId = currentIteration.sourceRefCommit.commitId;
                if (currentIteration.baseCommit) {
                    originalCommitId = currentIteration.baseCommit.commit.commitId;
                }
            }
        }

        if (baseIterationId >= 0) {
            const baseIteration = this.iterations.value.find(it => it.id === baseIterationId);
            if (baseIteration) {
                originalCommitId = baseIteration.sourceRefCommit.commitId;
            }
        }

        return {
            originalVersion: commitToVersionDescriptor(originalCommitId),
            modifiedVersion: commitToVersionDescriptor(modifiedCommitId)
        };
    }

    public getViewWidgets(): IReadonlyObservableArray<IEditorWidgetRegistration> {
        return this.viewWidgets;
    }

    public isRootItem(change: IChange): boolean {
        // Not used for PR details, safe to return false.
        return false;
    }

    public removeReviewer(reviewerId: string): void {
        if (this.reviewerIds.has(reviewerId)) {
            getGitClient(this.pageContext)
                .deletePullRequestReviewer(this.targetRepository.id, this.pullRequest.pullRequestId, reviewerId, this.targetRepository.project.id)
                .then(result => {
                    this.reviewerIds.delete(reviewerId);
                    this.reviewers.removeAll(x => x.identity.id === reviewerId);
                });
        }
    }

    public removeTag(tag: string) {
        // Delete tags by id if possible. Deleting by raw name can fail if the tag
        // contains "dangerous" characters in the path, even if they are encoded.
        const tagRef = this.tagRefs.find(t => t.name === tag);
        getGitClient(this.pageContext)
            .deletePullRequestLabels(this.targetRepository.id, this.pullRequest.pullRequestId, tagRef ? tagRef.id : tag)
            .then(() => {
                this.tagRefs = this.tagRefs.filter(t => t.name !== tag);
                this.tags.removeAll(x => x === tag);
            });
    }

    public restartMerge(): void {
        this.dataService
            .gitClientUpdatePullRequest({ mergeStatus: PullRequestAsyncStatus.Queued }, PullRequestDataTypes.policies | PullRequestDataTypes.statuses)
            .then(() => {
                const globalMessageService = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");

                const message =
                    this.pullRequest.mergeStatus === PullRequestAsyncStatus.Succeeded ? Resources.MergeUpdateNoOp : Resources.MergeUpdateQueued;

                globalMessageService.addToast({
                    duration: 3000,
                    message
                });
            });
    }

    public revertLocalChanges(): void {
        this.getPullRequestActionsService().then(prActionsService => prActionsService.revertLocalChanges());
    }

    public revertPullRequest(): void {
        this.getChangesActionsService().then(actionsService =>
            actionsService.revertPullRequest(this.pullRequest.pullRequestId, this.targetRepository, this.sourceBranch, this.targetBranch.value)
        );
    }

    public revertTitle(): void {
        this.setTitle(this.pullRequest.title);
    }

    public async saveDescription(): Promise<void> {
        if (this.description.value !== (this.pullRequest.description || "")) {
            const descriptionValue = useStorageKeysInMentions(this.pageContext)
                ? translateDisplayNamesToStorageKeys(this.pageContext, this.description.value)
                : this.description.value;

            const description = await tryReplacingAttachments(this.pageContext, descriptionValue, {
                projectId: this.targetRepository.project.id,
                pullRequestId: this.pullRequest.pullRequestId,
                repositoryId: this.targetRepository.id
            });

            this.dataService.gitClientUpdatePullRequest({ description });
        }

        this.descriptionEditMode.value = false;
    }

    public async saveTitle(): Promise<void> {
        if (this.title.value !== this.pullRequest.title) {
            this.dataService.gitClientUpdatePullRequest({ title: this.title.value });
        }
    }

    public selectIteration(iterationId: number, baseId: number): void {
        if (this.selectedIterationId.value !== iterationId || this.baseIterationId.value !== baseId) {
            this.baseIterationId.value = baseId;
            this.selectedIterationId.value = iterationId;

            // When user changes iteration, assume new changes banner has been already read.
            const messagesService = getReposMessagesService(this.pageContext);
            const message = messagesService.getMessage().value;
            if (message && message.type === PullRequestMessageType.newChanges) {
                messagesService.dismissMessage();
            }

            // This will update changes as a response to filters applied.
            this.updateIterationFilterText();

            // Set iteration and refresh discussions and changes (if not cached)
            this.dataService.setIteration(iterationId, baseId);

            let dataTypes = PullRequestDataTypes.discussionThreads;
            const key = `${iterationId}-${baseId}`;
            if (!this.iterationChangesCache[key]) {
                dataTypes |= PullRequestDataTypes.changes;
            }

            this.dataService.refreshPullRequestData(dataTypes);
        }
    }

    public setActivityFeedFilter(filter: string): void {
        this.activityFeedFilter = filter;
    }

    public setAutoCompleteIgnore(ignore: boolean, configurationIds: number[]): void {
        const completionOptions = (this.pullRequest && this.pullRequest.completionOptions) || {};

        const currentIds = completionOptions.autoCompleteIgnoreConfigIds;
        const autoCompleteIgnoreConfigIds = updateIgnoreList(currentIds, configurationIds, ignore);

        this.dataService.gitClientUpdatePullRequest(
            {
                completionOptions: {
                    ...completionOptions,
                    autoCompleteIgnoreConfigIds
                }
            },
            PullRequestDataTypes.autoComplete | PullRequestDataTypes.policies | PullRequestDataTypes.statuses
        );
    }

    public setChangeViewed(change: IChange, isViewed: boolean): void {
        const fileChanges = [];
        const changes = this.changes.value;

        const folderPrefix = change.path + (change.path.endsWith("/") ? "" : "/");
        // Update files under the path (possibly just 1), and collect list of files to update on the server.
        for (const path in changes.changesByPath) {
            if ((path.startsWith(folderPrefix) || path === change.path) && changes.changesByPath[path]) {
                const c = changes.changesByPath[path]!;
                (c.checkmarked as ObservableValue<boolean>).value = isViewed;
                fileChanges.push(c);
            }
        }

        // Update folders under the path (if any)
        for (const path in changes.treeItemsByPath) {
            if ((path.startsWith(folderPrefix) || path === change.path) && changes.treeItemsByPath[path]) {
                const treeItem = changes.treeItemsByPath[path]!;
                (treeItem.data.checkmarked as ObservableValue<boolean>).value = isViewed;
            }
        }

        // Update folders upwards till root
        let parentPath = change.path;
        while (parentPath !== "/") {
            parentPath = parentPath.substring(0, parentPath.lastIndexOf("/")) || "/";
            const treeItem = changes.treeItemsByPath[parentPath];

            if (treeItem) {
                const newValue = treeItem.childItems!.every(child => child.data.checkmarked!.value);
                if (treeItem.data.checkmarked!.value === newValue) {
                    break;
                }

                (treeItem.data.checkmarked as ObservableValue<boolean>).value = newValue;
            }
        }

        const updateRequest = getUpdateProperties(this.pullRequest, fileChanges, isViewed);
        updateVisitData(this.pageContext, updateRequest);

        for (const key of updateRequest.modifyHashes) {
            this.viewedState.hashes[key] = updateRequest.modifyViewedStatus;
        }

        this.refreshViewedCount();

        const viewedFilter = this.filesFilter.getAppliedFilterItemState(FilesFilterKeys.viewedFile);
        if (viewedFilter && viewedFilter.value && viewedFilter.value[0] !== FilesFilterViewedKeys.all) {
            // Apply filter again to drop the files that have toggled
            this.applyFilesFilter(this.filesFilter.getAppliedState(), "filter-applied");
        }
    }

    public setDescription(description: string): void {
        this.description.value = description;
    }

    public setDescriptionEditMode(editMode: boolean): void {
        this.descriptionEditMode.value = editMode;

        this.description.value = this.description.value || this.pullRequest.description || "";

        if (!editMode) {
            this.description.value = this.pullRequest.description || "";
        }
    }

    public setDraftMode(isDraft: boolean): void {
        this.dataService.gitClientUpdatePullRequest(
            { isDraft },
            PullRequestDataTypes.policies | PullRequestDataTypes.statuses | PullRequestDataTypes.autoComplete
        );
    }

    public setLiveUpdatesEnabled(enabled: boolean): void {
        this.liveUpdatesEnabled.value = enabled;
        this.updateLiveUpdates();
    }

    public refreshWithCacheInvalidation(): void {
        this.dataService.refreshPullRequestData(
            PullRequestDataTypes.reinvalidatePolicyCache
        );
    }

    public setSelectedTabId(tabId: string): void {
        this.selectedTabId.value = tabId;
    }

    public setStatus(status: PullRequestStatus): void {
        this.dataService.gitClientUpdatePullRequest(
            { status },
            PullRequestDataTypes.policies | PullRequestDataTypes.statuses | PullRequestDataTypes.autoComplete
        );
    }

    public setTitle(title: string): void {
        this.title.value = title;
        this.isTitleDirty.value = title !== this.pullRequest.title;

        const navService = this.pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
        navService.setPageTitle(format(Resources.DocumentTitleFormat, this.pullRequest.pullRequestId, title));
    }

    public async setVote(vote: ReviewerVote, hasDeclined = false, isReapprove = false) {
        const userId = this.currentUserId;
        const existingReviewer = this.reviewers.value.filter(r => r.identity.id === userId)[0];

        const discussionService = getPullRequestDiscussionService(this.pageContext);
        if (discussionService.hasUnsavedThreads()) {
            const saveComments = await new Promise<boolean>(resolve => {
                showMessageDialog(this.pageContext, {
                    lightDismiss: false,
                    title: Resources.VoteWithUnsavedCommentsTitle,
                    message: Resources.VoteWithUnsavedCommentsMessage,
                    okButtonProps: { primary: true, text: Resources.SaveComments },
                    cancelButtonProps: { text: Resources.CancelVote },
                    onClose: resolve
                });
            });

            if (saveComments) {
                await discussionService.saveThreads();
            } else {
                return;
            }
        }

        if (vote === ReviewerVote.ApprovedWithSuggestion && this.pullRequest.autoCompleteSetBy) {
            const cancelAutocomplete = await new Promise<boolean>(resolve => {
                showMessageDialog(this.pageContext, {
                    lightDismiss: false,
                    title: Resources.CancelAutoCompleteTitle,
                    message: Resources.CancelAutoCompleteMessage,
                    okButtonProps: { primary: true, text: Resources.CancelAutoComplete },
                    cancelButtonProps: { text: Resources.KeepAutoComplete },
                    onClose: resolve
                });
            });

            if (cancelAutocomplete) {
                await this.cancelAutoComplete();
            }
        }

        if (!existingReviewer || existingReviewer.vote !== vote || isReapprove) {
            getGitClient(this.pageContext)
                .createPullRequestReviewer(
                    {
                        hasDeclined,
                        id: userId,
                        isReapprove,
                        isRequired: existingReviewer ? existingReviewer.baseReviewer.isRequired : undefined,
                        vote
                    } as IdentityRefWithVote,
                    this.targetRepository.id,
                    this.pullRequest.pullRequestId,
                    userId,
                    this.targetRepository.project.id
                )
                .then(reviewer => {
                    const reviewerIndex = this.reviewers.value.findIndex(r => r.identity.id === userId);
                    if (reviewerIndex >= 0) {
                        const voteMap = this.createVotedByForMap(
                            ...this.reviewers.value.map(x => x.baseReviewer).filter(r => r.id !== userId),
                            reviewer
                        );
                        this.reviewers.change(reviewerIndex, this.convertToIReviewer(reviewer, voteMap));
                    }

                    this.dataService.refreshPullRequestData(PullRequestDataTypes.policies | PullRequestDataTypes.autoComplete);
                });
        }
    }

    public isApprovedByCurrentUser(): boolean {
        const currentUserReviews = this.reviewers.value.filter(review => review.identity.id === this.currentUserId);
        return currentUserReviews.length > 0 && currentUserReviews[0].vote == ReviewerVote.Approved;
    }

    public sharePullRequest(): void {
        this.getPullRequestActionsService().then(actionsService => {
            actionsService.share();
        });
    }

    public showChecksPanel(): void {
        this.getPullRequestActionsService().then(actionsService => {
            actionsService.showChecksPanel();
        });
    }

    public startLiveUpdates(): void {
        this.updateLiveUpdates();
    }

    public toggleIsFollowed(): void {
        if (this.subscriptionId) {
            getNotificationClient(this.pageContext)
                .deleteSubscription(this.subscriptionId)
                .then(() => {
                    this.subscriptionId = "";
                    this.isFollowed.value = false;
                });
        } else {
            getNotificationClient(this.pageContext)
                .createSubscription({
                    filter: {
                        artifactId: this.getNotificationArtifactId(),
                        artifactType: "PullRequestId",
                        eventType: null as any,
                        type: "Artifact"
                    } as ISubscriptionFilter
                } as NotificationSubscriptionCreateParameters)
                .then(subscription => {
                    this.subscriptionId = subscription.id;
                    this.isFollowed.value = true;
                });
        }
    }

    public toggleReviewerFlag(reviewer: IReviewer): void {
        const requestReviewer: IdentityRefWithVote = {
            ...reviewer.baseReviewer,
            isFlagged: !reviewer.baseReviewer.isFlagged
        };

        getGitClient(this.pageContext)
            .updatePullRequestReviewer(
                requestReviewer,
                this.targetRepository.id,
                this.pullRequest.pullRequestId,
                reviewer.identity.id,
                this.targetRepository.project.id
            )
            .then(() => {
                this.dataService.refreshPullRequestData(PullRequestDataTypes.basicDetails);
            });
    }

    public updateDeclineToReview(decline: boolean): void {
        const reviewer = this.reviewers.value.find(r => r.identity.id === this.currentUserId);
        if (!reviewer) {
            if (decline) {
                // We need to create a reviewer to remember the decline bit.
                this.setVote(ReviewerVote.NoVote, true);
            }

            return;
        }

        const requestReviewer: IdentityRefWithVote = {
            ...reviewer.baseReviewer,
            hasDeclined: decline
        };

        getGitClient(this.pageContext)
            .updatePullRequestReviewer(
                requestReviewer,
                this.targetRepository.id,
                this.pullRequest.pullRequestId,
                reviewer.identity.id,
                this.targetRepository.project.id
            )
            .then(() => {
                this.dataService.refreshPullRequestData(PullRequestDataTypes.basicDetails);
            });
    }

    public updateReviewerRequired(reviewer: IReviewer, required: boolean): void {
        getGitClient(this.pageContext)
            .createPullRequestReviewer(
                {
                    id: reviewer.identity.id,
                    isRequired: required,
                    vote: reviewer.identity.id === this.currentUserId ? reviewer.vote : 0
                } as IdentityRefWithVote,
                this.targetRepository.id,
                this.pullRequest.pullRequestId,
                reviewer.identity.id,
                this.targetRepository.project.id
            )
            .then(resolvedReviewer => {
                const reviewerIndex = this.reviewers.value.findIndex(r => r.identity.id === reviewer.identity.id);
                if (reviewerIndex >= 0) {
                    const voteMap = this.createVotedByForMap(...this.reviewers.value.map(x => x.baseReviewer));
                    this.reviewers.change(reviewerIndex, this.convertToIReviewer(resolvedReviewer, voteMap));
                }
            }).catch(error => {
                const errorMessage = error.serverError ? error.serverError.message : error.message;

                const messagesService = getReposMessagesService(this.pageContext);
                messagesService.showMessage(
                    {
                        buttons: [{ id: "try-again", onClick: () => this.updateReviewerRequired(reviewer, required), text: Resources.TryAgain }],
                        renderMessage: () => <span>{errorMessage}</span>,
                        severity: MessageCardSeverity.Error,
                        type: PullRequestMessageType.reviewersError
                    },
                    false
                );
            });
    }

    public async loadAdornmentProviders(): Promise<void> {
        // Fetch the adornment contribution asynchronously rather than via page include since existing adorments
        // carry large dependencies via includes which we don't want on initial page load.
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        await contributionService.getContributionAsync("ms.vss-code-web.content-pr-file-adornment");

        const adornmentContributions = contributionService.getContributionChildren("ms.vss-code-web.content-pr-file-adornment") || [];
        for (const adornmentContribution of adornmentContributions) {
            const service = await contributionService.getService<IPullRequestAdornmentService>(adornmentContribution);
            if (service) {
                this.adornmentServices.push(service);
                service.initializeExtension({
                    organisationName: this.hostName,
                    projectId: this.targetRepository.project.id,
                    pullRequestId: this.pullRequest.pullRequestId,
                    repositoryId: this.targetRepository.id
                });
            }
        }

        this.loadAdornments();
    }

    public getWitTransitionWarningMessage(): ObservableValue<WorkItemTransitionWarning | undefined> {
        return this.transitionWarningMessage;
    }

    public getBlockingPolicyMessage(): string {
        const messages: string[] = [];
        const addedPolicyIds: { [id: string]: boolean } = {};

        const blockingPolicies = getBlockingPolicies(this.dataService.getPolicies().value);
        // We may have more than one evaluation for approvalCount if we inherit policies (e.g. releases/* requires 2 but releases/patches/* requires 3)
        // It gets most restrictive approval count evaluation
        const defaultApprovalCountPolicy = blockingPolicies
            .find(p => p.policyType.id === PolicyTypeId.approvalCount) as IApproverCountPolicyEvaluation | undefined;
        const approvalCountPolicy = blockingPolicies.filter(p => p.policyType.id === PolicyTypeId.approvalCount)
            .map (p => p as IApproverCountPolicyEvaluation)
            .reduce((prev, current) => {
                if (!prev)
                    return current;

                const isMoreRestrict = (prev.status === PolicyEvaluationStatus.Approved && current.status !== PolicyEvaluationStatus.Approved) ||
                    prev.minimumApproverCount < current.minimumApproverCount ||
                    (prev.minimumApproverCount === current.minimumApproverCount && prev.creatorVoteCounts && !current.creatorVoteCounts)

                return isMoreRestrict ? current : prev;
            }, defaultApprovalCountPolicy)

        for (const policy of blockingPolicies) {
            if (!addedPolicyIds[policy.policyType.id]) {
                if (policy.policyType.id === PolicyTypeId.approvalCount && policy !== approvalCountPolicy)
                    continue;

                messages.push(policy.displayText);
                addedPolicyIds[policy.policyType.id] = true;
            }
        }
        return messages.join(", ");;
    }

    private updateTransitionWarningMessage(): void {
        const workItemTransitionsWithProblems: WorkItemNextStateOnTransition[] =
            this.relatedWitTransitionItems.value.filter(transition => transition.message);

        if (!workItemTransitionsWithProblems.length) {
            this.transitionWarningMessage.value = undefined;
            this.relatedWitTransitionItems.value = [];
            return;
        }

        const transitionToShow: WorkItemNextStateOnTransition = workItemTransitionsWithProblems[0];
        const hasOverflowMessage: boolean = workItemTransitionsWithProblems.length > 1;
        const overflowMessage: string = workItemTransitionsWithProblems.length > 2
            ? format(Resources.PullRequest_CompleteMergeDialog_WorkItemTransitionWarningOverflowPlural, workItemTransitionsWithProblems.length - 1)
            : Resources.PullRequest_CompleteMergeDialog_WorkItemTransitionWarningOverflowSingular;

        this.transitionWarningMessage.value = {
            message: transitionToShow.message,
            link: (transitionToShow.errorCode && this.TRANSITION_ERROR_LINKS[transitionToShow.errorCode]),
            overflowMessage: (hasOverflowMessage && overflowMessage) || " "
        };
    }

    private sendCreateReviewerRequest(identity: IIdentity, required: boolean): Promise<IdentityRefWithVote> {
        if (identity.localId) {
            return getGitClient(this.pageContext).createPullRequestReviewer(
                { ...identity, isRequired: required, vote: 0 } as unknown as IdentityRefWithVote,
                this.targetRepository.id,
                this.pullRequest.pullRequestId,
                identity.localId,
                this.targetRepository.project.id
            );
        }

        if (identity.originDirectory.toLowerCase() === "aad") {
            return getGitClient(this.pageContext).createUnmaterializedPullRequestReviewer(
                {
                    ...identity,
                    isRequired: required,
                    vote: 0,
                    isAadIdentity: true,
                    id: identity.originId,
                    isContainer: equals(identity.entityType, "group", true)
                } as unknown as IdentityRefWithVote,
                this.targetRepository.id,
                this.pullRequest.pullRequestId,
                this.targetRepository.project.id
            );
        }

        return Promise.reject({ message: format(Resources.ReviewerCouldNotBeAdded, identity.displayName) });
    }

    private applyFilesFilter = (filterState: IFilterState, action: string) => {
        if (action === "filter-applied") {
            if (filterState[FilesFilterKeys.iteration]) {
                const iterationFilterValue = filterState[FilesFilterKeys.iteration]!.value;
                if (!iterationFilterValue && (this.selectedIterationId.value !== this.latestIterationId || this.baseIterationId.value !== 0)) {
                    // If iteration filter has been cleared out, restore latest changes
                    this.selectIteration(this.latestIterationId, 0);
                }
            }

            // This updates the comments under files based on the comment filter state
            if (
                filterState[DiscussionFilterKeys.commentOwner] ||
                filterState[DiscussionFilterKeys.commentPreference] ||
                filterState[DiscussionFilterKeys.commentStatus]
            ) {
                getDiscussionFilterService(this.pageContext).applyFilter(filterState);
            }

            // This updates the files based on the files filter state
            this.updateChanges(this.iterationChangesCache[`${this.selectedIterationId.value}-${this.baseIterationId.value}`]);
        }
    };

    private updateCompletionData(pullRequest: GitPullRequest, autoCompleteBlockingPolicyIds: number[]): void {
        const routingService = getReposRoutingService(this.pageContext);
        const completionStatus: ICompletionStatus = {
            completedData:
                pullRequest.status === PullRequestStatus.Completed
                    ? {
                          identity: pullRequest.closedBy,
                          date: pullRequest.closedDate,
                          mergeCommit:
                              pullRequest.lastMergeCommit &&
                              this.convertToICommit(pullRequest.lastMergeCommit, this.targetRepository, routingService),
                          completionOptions: pullRequest.completionOptions || {}
                      }
                    : undefined,
            abandonedData:
                pullRequest.status === PullRequestStatus.Abandoned
                    ? {
                          date: pullRequest.closedDate
                      }
                    : undefined,
            completingData:
                pullRequest.status === PullRequestStatus.Active && pullRequest.completionQueueTime
                    ? {
                          isQueued: pullRequest.mergeStatus === PullRequestAsyncStatus.Queued
                      }
                    : undefined,
            completionOptions: pullRequest.completionOptions || {},
            autoCompletingData:
                pullRequest.status === PullRequestStatus.Active && pullRequest.autoCompleteSetBy
                    ? {
                          autoCompleteSetBy: pullRequest.autoCompleteSetBy,
                          blockingPolicyIds: autoCompleteBlockingPolicyIds,
                          completionOptions: pullRequest.completionOptions || {}
                      }
                    : undefined,
            externallyMerged: pullRequest.commits && pullRequest.commits.length === 0,
            isSourceDeleted: this.isSourceDeleted,
            isTargetDeleted: this.isTargetDeleted,
            mergeFailureData:
                pullRequest.mergeStatus === PullRequestAsyncStatus.Failure || pullRequest.mergeStatus === PullRequestAsyncStatus.RejectedByPolicy
                    ? {
                          message: pullRequest.mergeFailureMessage ?? "Unknown error"
                      }
                    : undefined,
            mergingData:
                pullRequest.mergeStatus === PullRequestAsyncStatus.NotSet || pullRequest.mergeStatus === PullRequestAsyncStatus.Queued
                    ? {
                          isQueued: pullRequest.mergeStatus === PullRequestAsyncStatus.Queued
                      }
                    : undefined,
            hasConflicts: pullRequest.mergeStatus === PullRequestAsyncStatus.Conflicts,
            lastMergeDate: pullRequest.lastMergeCommit && pullRequest.lastMergeCommit.author.date
        };

        if (completionStatus.hasConflicts) {
            const maxConflictFiles = 10;
            getGitClient(this.pageContext)
                .getPullRequestConflicts(
                    this.targetRepository.id,
                    pullRequest.pullRequestId,
                    this.targetRepository.project.id,
                    0,
                    maxConflictFiles + 1,
                    false,
                    true,
                    false
                )
                .then(conflicts => {
                    if (conflicts.length > maxConflictFiles) {
                        this.conflictsData.value = { conflicts: [], tooMany: true };
                    } else {
                        this.conflictsData.value = { conflicts, hasSecondOrderConflicts: conflicts.length === 0 };
                    }
                });
        } else if (this.conflictsData.value) {
            this.conflictsData.value = undefined;
        }

        this.completingTimer.clearAllTimers();
        if (completionStatus.completingData) {
            // We rely on SignalR to refresh the UI when the PR is completed in the server. But:
            // a) SignalR may fail.
            // b) We've seen cases where SignalR works but the fetch still returns a PR with status=1 (stale).
            // As a safety net, let's refresh after more than 5 seconds which is the throttling of PullRequestUpdatesService.
            this.completingTimer.setTimeout(() => this.dataService.refreshPullRequestData(PullRequestDataTypes.basicDetails), 7000);
        }

        if (!this.completionStatus) {
            this.completionStatus = new ObservableValue(completionStatus);
        } else {
            this.completionStatus.value = completionStatus;
        }
    }

    private checkFullscreenValidity = () => {
        if (this.getSelectedTabId().value !== "files") {
            // make sure we turn off fullscreen mode if it was on when leaving files tab
            const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            if (layoutManager.getFullScreenMode().value) {
                layoutManager.setFullScreenMode(false);
            }
        }
    };

    private convertToIBasicIdentityRef(identityRef: IdentityRef): IBasicIdentityRef {
        const { _links, imageUrl } = identityRef;
        const personaIdentity: IBasicIdentityRef = { id: identityRef.id, displayName: identityRef.displayName };

        if (_links && _links.avatar && _links.avatar.href) {
            personaIdentity.imageUrl = _links.avatar.href;
        } else if (imageUrl) {
            personaIdentity.imageUrl = imageUrl;
        }

        return personaIdentity;
    }

    private convertToICommit(commit: GitCommitRef, repository: GitRepository, routingService: IReposRoutingService): ICommit {
        return {
            comment: commit.comment,
            commit,
            linkProps: {
                href: routingService.getCommitUrl(repository.name, repository.project.name, commit.commitId, branchNameToRef(this.sourceBranch))
            },
            repositoryId: repository.id,
            shortCommitId: getShortCommitId(commit.commitId)
        };
    }

    private convertToIIteration(
        iteration: GitPullRequestIteration,
        showBaseCommit: boolean,
        connectUpwards: boolean,
        routingService: IReposRoutingService
    ): IIteration {
        return {
            author: iteration.author,
            baseCommit: iteration.commonRefCommit && this.convertToICommit(iteration.commonRefCommit, this.sourceRepository, routingService),
            commits: (iteration.commits || []).map(c => this.convertToICommit(c, this.sourceRepository, routingService)),
            commitsUrl: routingService.getBranchCompareUrl(
                this.sourceRepository.name,
                this.sourceRepository.project.name,
                commitToVersionDescriptor(iteration.targetRefCommit.commitId),
                commitToVersionDescriptor(iteration.sourceRefCommit.commitId)
            ),
            continuePrevious: connectUpwards,
            description: iteration.description,
            hasMoreCommits: iteration.hasMoreCommits,
            id: iteration.id,
            newTargetRefName: iteration.newTargetRefName,
            oldTargetRefName: iteration.oldTargetRefName,
            reason: iteration.reason,
            showBaseCommit,
            sourceRefCommit: iteration.sourceRefCommit,
            updatedDate: iteration.updatedDate
        };
    }

    private convertToIReviewer(reviewer: IdentityRefWithVote, votedForByMap: { [key: string]: IdentityRefWithVote[] }): IReviewer {
        const votedForBy = votedForByMap[reviewer.id];
        const baseVoteText = reviewer.hasDeclined ? Resources.DeclinedToReview : getVoteText(reviewer.vote as ReviewerVote);
        const policies = this.reviewerPolicyLookup.value[reviewer.id] || [];
        const strictestPolicy = policies.reduce<IRequiredReviewersPolicyEvaluation | undefined>((previous, current) => {
            if (!previous || (current.isBlocking && !previous.isBlocking)) {
                return current;
            }
            if (previous.isBlocking && !current.isBlocking) {
                return previous;
            }
            return previous.minimumApproverCount >= current.minimumApproverCount ? previous : current;
        }, undefined);

        const excludeAuthor = !strictestPolicy?.creatorVoteCounts;
        const viaReviewer =
            votedForBy &&
            reviewer.vote !== ReviewerVote.NoVote &&
            votedForBy.filter(r => r.vote === reviewer.vote && !(excludeAuthor && r.id === this.getCreator().id))[0];

        let voteText = viaReviewer ? format(Resources.VoteViaFormat, baseVoteText, viaReviewer.displayName) : baseVoteText;
        let vote = reviewer.vote as ReviewerVote;
        // For groups, confirm that it has all the required votes.
        // > A group approval only means that at least one member of the group has approved.
        // > But there may exist stronger policies defined in terms of minimumApproverCount and creatorVoteCounts.
        // > These settings are not considered when a group is marked approved, so we need to check here.
        if (strictestPolicy && hasApproved(reviewer.vote) && reviewer.isContainer) {
            const votesCount = getValidApprovalVotes(votedForBy, strictestPolicy, this.getCreator().id);
            const creatorApproved = votedForBy && votedForBy.some(voter => voter.id === this.getCreator().id && hasApproved(voter.vote));
            const template =
                creatorApproved && !strictestPolicy.creatorVoteCounts
                    ? Resources.GroupPartialApprovalCreatorExcluded
                    : Resources.GroupPartialApproval;

            if (votesCount < strictestPolicy.minimumApproverCount) {
                voteText = format(template, votesCount, strictestPolicy.minimumApproverCount);
                vote = ReviewerVote.NoVote;
            }
        }

        let policyUrl = this.getTargetBranchPolicyUrl();

        if (strictestPolicy) {
            policyUrl = `${policyUrl}&reviewer=${strictestPolicy.configurationId}`;
        }

        return {
            baseReviewer: reviewer,
            identity: this.convertToIBasicIdentityRef(reviewer),
            policyUrl,
            strictestPolicy,
            vote,
            votedForBy,
            voteStatusProps: getVoteStatusProps(vote),
            voteText: voteText
        };
    }

    private createVotedByForMap(...reviewers: IdentityRefWithVote[]) {
        // Create a map of groups that were voted on behalf of to the person who voted for them
        const votedForByMap: { [key: string]: IdentityRefWithVote[] } = {};
        reviewers.forEach(reviewer => {
            reviewer.votedFor &&
                reviewer.votedFor.forEach(votedFor => {
                    if (!votedForByMap[votedFor.id]) {
                        votedForByMap[votedFor.id] = [];
                    }
                    votedForByMap[votedFor.id].push(reviewer);
                });
        });

        return votedForByMap;
    }

    private async loadAdornments(): Promise<void> {
        // Reset existing adornments
        if (this.viewWidgets.length) {
            this.viewWidgets.value = [];
        }

        const navigationService: IGitNavigationService = getGitNavigationService(this.pageContext);
        const selectedItem: IReadonlyObservableValue<IChange> = navigationService.getSelectedItem();

        const newAdornments: IGutterAdornment[] = [];
        const path = selectedItem.value.path;
        const changeType = selectedItem.value.changeType;

        // Only load adornments for individual files, and not for deletes
        if (selectedItem.value.isFolder || (changeType & VersionControlChangeType.Delete) !== 0) {
            return;
        }

        // Code coverage adornment expects oPath to be empty for new files but not edits
        const isAdd = (changeType & VersionControlChangeType.Add) !== 0;
        const oPath = isAdd ? "" : selectedItem.value.sourcePath || path;

        for (const service of this.adornmentServices) {
            try {
                const collection = await service.getGutterAdornments({
                    baseIterationId: this.baseIterationId.value,
                    iterationId: this.selectedIterationId.value,
                    mPath: path,
                    oPath
                });
                if (collection) {
                    const adornmentBlockToAdornment = (adornment: IGutterAdornmentBlock, isOriginalSide: boolean): IGutterAdornment => {
                        let statusClassName = "";
                        switch (adornment.IndicatorType) {
                            case IndicatorType.Error:
                                statusClassName = "failed";
                                break;
                            case IndicatorType.Succeeded:
                                statusClassName = "succeeded";
                                break;
                            case IndicatorType.Warning:
                                statusClassName = "warning";
                                break;
                        }

                        return {
                            id: id++,
                            startLine: adornment.LineNumberStart,
                            endLine: adornment.LineNumberStart + adornment.LinesCount,
                            isOriginalSide,
                            className: "pr-gutter-adornment " + statusClassName,
                            helpText: adornment.HelpText
                        };
                    };

                    let id = 0;
                    if (collection.originalFilegutterAdornments) {
                        // Code coverage provider returns "original" adornments for new files for some reason.
                        const originalSide = !isAdd;
                        for (const adornment of collection.originalFilegutterAdornments) {
                            newAdornments.push(adornmentBlockToAdornment(adornment, originalSide));
                        }
                    }
                    if (collection.modifiedFilegutterAdornments) {
                        for (const adornment of collection.modifiedFilegutterAdornments) {
                            newAdornments.push(adornmentBlockToAdornment(adornment, false));
                        }
                    }
                }
            } catch (ex) {
                console.error(`Failed to get gutter adornments for file ${path}: ${ex.message}`);
            }
        }

        // There are asynchronous calls above, so make sure we are still looking at the same file
        if (newAdornments.length && selectedItem.value.path === path) {
            this.viewWidgets.push(getGutterAdornmentWidgetRegistration(newAdornments));
        }
    }

    private getNotificationArtifactId(): string {
        return `${this.targetRepository.project.id}%2f${this.targetRepository.id}%2f${this.pullRequest.pullRequestId}`;
    }

    private async getChangesActionsService(): Promise<IChangesActionsService> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return (await contributionService.getService<IChangesActionsService>("ms.vss-code-web.changes-actions-service"))!;
    }

    private async getPullRequestActionsService(): Promise<IPullRequestActionsService> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return (await contributionService.getService<IPullRequestActionsService>("ms.vss-code-web.pr-actions-service"))!;
    }

    private async getUpdatesService(): Promise<IPullRequestUpdatesService> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return (await contributionService.getService<IPullRequestUpdatesService>("ms.vss-code-web.pr-updates-service"))!;
    }

    private updateAutoCompleteData = (autoCompleteData: IPullRequestAutoCompleteData | undefined): void => {
        if (!autoCompleteData || !autoCompleteData.blockingPolicyIds) {
            this.autoCompleteBlockingPolicyIds = [];
        } else {
            this.autoCompleteBlockingPolicyIds = autoCompleteData.blockingPolicyIds;
        }

        this.updateCompletionData(this.pullRequest, this.autoCompleteBlockingPolicyIds);
    };

    private updatePullRequest = (pullRequest: GitPullRequest): void => {
        const previousPullRequest = this.pullRequest;

        this.pullRequest = pullRequest;

        setPermissions(this.pageContext, pullRequest);

        if (pullRequest.isDraft !== this.isDraft.value) {
            this.isDraft.value = pullRequest.isDraft;
        }
        if (this.status.value !== pullRequest.status) {
            this.status.value = pullRequest.status;
        }
        if (!previousPullRequest || previousPullRequest.title !== pullRequest.title) {
            this.setTitle(pullRequest.title);
        }

        const updateDescription = (newDescription: string) => {
            if (!previousPullRequest || previousPullRequest.description !== newDescription) {
                this.description.value = newDescription;
            }
        };

        if (useStorageKeysInMentions(this.pageContext)) {
            translateStorageKeysToDisplayNames(this.pageContext, pullRequest.description).then((translatedText: string) => {
                updateDescription(translatedText);
                pullRequest.description = translatedText;
            });
        } else {
            updateDescription(pullRequest.description);
        }

        this.targetRepository = pullRequest.repository;
        this.sourceRepository = this.targetRepository;
        if (pullRequest.forkSource) {
            this.sourceRepository = pullRequest.forkSource.repository;
            if (!this.sourceRepository.project) {
                this.sourceRepository.project = this.targetRepository.project;
            }
        }

        this.sourceBranch = refToBranchName(pullRequest.forkSource ? pullRequest.forkSource.name : pullRequest.sourceRefName);
        const targetBranchName = refToBranchName(pullRequest.targetRefName);
        if (this.targetBranch.value !== targetBranchName) {
            this.targetBranch.value = targetBranchName;
        }

        this.updateCompletionData(pullRequest, this.autoCompleteBlockingPolicyIds);

        this.reviewerIds = new Set(pullRequest.reviewers.map(x => x.id));
        this.updateReviewersAndPolicies(pullRequest.reviewers || []);

        const routingService = getReposRoutingService(this.pageContext);

        const allCommits = pullRequest.commits || [];
        this.commits.value = allCommits.slice(0, maxCommits).map(c => this.convertToICommit(c, this.sourceRepository, routingService));
        this.allCommitsUrl.value =
            allCommits.length > maxCommits
                ? routingService.getBranchCompareUrl(
                      this.sourceRepository.name,
                      this.sourceRepository.project.name,
                      branchToVersionDescriptor(refToBranchName(pullRequest.targetRefName)),
                      branchToVersionDescriptor(refToBranchName(pullRequest.sourceRefName))
                  )
                : undefined;

        if (this.pullRequest.lastMergeSourceCommit && this.pullRequest.mergeStatus !== PullRequestAsyncStatus.Queued) {
            this.noIterationsSourceCommitId = this.pullRequest.lastMergeSourceCommit.commitId;
        }

        if (this.pullRequest.lastMergeTargetCommit && this.pullRequest.mergeStatus !== PullRequestAsyncStatus.Queued) {
            this.noIterationsTargetCommitId = this.pullRequest.lastMergeTargetCommit.commitId;
        }
    };

    private updatePullRequestAndRefs = (data: IPullRequestBasicData): void => {
        const pullRequest = data.pullRequest;
        this.sourceRefStatus = data.sourceRefStatus;
        this.sourceRef = data.sourceRefs && data.sourceRefs[0];
        this.isSourceDeleted = !this.sourceRef;
        this.isTargetDeleted = !data.targetRefs || data.targetRefs.length === 0;

        this.updatePullRequest(pullRequest);

        this.noIterationsSourceCommitId = "";
        if (this.pullRequest.lastMergeSourceCommit && this.pullRequest.mergeStatus !== PullRequestAsyncStatus.Queued) {
            this.noIterationsSourceCommitId = this.pullRequest.lastMergeSourceCommit.commitId;
        } else if (data.sourceRefs && data.sourceRefs.length) {
            this.noIterationsSourceCommitId = data.sourceRefs[0].objectId;
        }

        this.noIterationsTargetCommitId = "";
        if (this.pullRequest.lastMergeTargetCommit && this.pullRequest.mergeStatus !== PullRequestAsyncStatus.Queued) {
            this.noIterationsTargetCommitId = this.pullRequest.lastMergeTargetCommit.commitId;
        } else if (data.targetRefs && data.targetRefs.length) {
            this.noIterationsTargetCommitId = data.targetRefs[0].objectId;
        }
    };

    private processChanges(
        changes: PullRequestIterationChanges | undefined,
        key: string = "",
        filterProps?: {
            viewedFilter?: FilesFilterViewedKeys;
            commentedFilter?: DiscussionFilterCommentedKeys;
        }
    ): IChangeCollection<GitVersionDescriptor> {
        const { originalVersion, modifiedVersion } = this.getVersionsForIterationRange(
            changes ? changes.iterationId : -1,
            changes ? changes.baseIterationId : -1
        );

        let commitChanges: IGitCommitChange[];
        let loadMoreChanges: { (): void } | undefined;
        if (changes) {
            commitChanges = changes.changeEntries.map(c => {
                return {
                    changeType: c.changeType,
                    commitId: c.item.commitId,
                    item: {
                        isFolder: c.item.isFolder,
                        isSymLink: c.item.isSymLink,
                        originalPath: c.originalPath,
                        path: c.item.path || c.originalPath,
                        trackingId: c.changeTrackingId
                    },
                    objectId: c.item.objectId
                };
            });
            if (changes.nextSkip > 0) {
                loadMoreChanges = () => {
                    this.dataService.loadMoreChanges();
                };
            }
        } else {
            commitChanges = [];
        }

        const checkmarksMap = buildCheckmarksMap(commitChanges, this.viewedState);

        const filtering = !!filterProps;
        const filterViewed = filtering ? filterProps?.viewedFilter : FilesFilterViewedKeys.all;
        const filterCommented = filtering ? filterProps?.commentedFilter : DiscussionFilterCommentedKeys.all;

        if (filterViewed !== FilesFilterViewedKeys.all) {
            const isViewed = filterViewed === FilesFilterViewedKeys.viewed;
            commitChanges = commitChanges.filter(c => checkmarksMap[c.item.path] === isViewed);
        }

        const discussionFilterService = getDiscussionFilterService(this.pageContext);
        if (filtering) {
            // Updates the counts based on the new changes
            discussionFilterService.updateFilePaths(commitChanges.map(c => c.item.path));
        }

        if (filterCommented !== DiscussionFilterCommentedKeys.all) {
            commitChanges = commitChanges.filter(c => discussionFilterService.fileContainsThread(c.item.path));
        }

        return getChangesCollection(
            commitChanges,
            this.targetRepository.name,
            modifiedVersion.version,
            modifiedVersion,
            originalVersion,
            loadMoreChanges,
            key,
            checkmarksMap
        );
    }

    private refreshViewedCount() {
        this.viewedCount.value = calculateViewedCount(
            Object.keys(this.latestChanges.value.changesByPath).map(path => this.latestChanges.value.changesByPath[path]!),
            this.viewedState
        );
    }

    private updateChanges = (changes: PullRequestIterationChanges | undefined) => {
        if (changes) {
            this.iterationChangesCache[`${changes.iterationId}-${changes.baseIterationId}`] = changes;

            const newPathsValue = changes.changeEntries.map(c => c.item.path || c.originalPath || "");
            if (this.allChangedPaths.value.join(",") !== newPathsValue.join(",")) {
                this.allChangedPaths.value = newPathsValue;
            }
        }

        const keywordFilterItemValue = this.filesFilter.getFilterItemValue<string>(FilesFilterKeys.keyword);
        if (changes && keywordFilterItemValue) {
            changes = {
                ...changes,
                changeEntries: changes.changeEntries.filter(c => caseInsensitiveContains(c.item.path || c.originalPath || "", keywordFilterItemValue))
            };
        }

        const viewedFilterItemValue = this.filesFilter.getFilterItemValue<Array<FilesFilterViewedKeys>>(FilesFilterKeys.viewedFile);
        const commentedFilterItemValue = this.filesFilter.getFilterItemValue<Array<DiscussionFilterCommentedKeys>>(FilesFilterKeys.commentedFile);

        let nextSkip = "";
        if (isFeatureFlagEnabled(this.pageContext, "SourceControl.PR.AddNextSkipToChangesKey", false))
        {
            nextSkip = `,${changes ? changes.nextSkip : ""}`
        }

        const key = changes
            ? `${changes.iterationId}:${changes.baseIterationId},${changes.changeEntries.length},${keywordFilterItemValue},${
                  viewedFilterItemValue ? viewedFilterItemValue[0] : FilesFilterViewedKeys.all
              },${commentedFilterItemValue ? commentedFilterItemValue[0] : DiscussionFilterCommentedKeys.all}${nextSkip}`
            : "";

        if (key === this.changesCurrentKey) {
            return;
        }

        const changesCollection = this.processChanges(changes, key, {
            viewedFilter: viewedFilterItemValue && viewedFilterItemValue[0],
            commentedFilter: commentedFilterItemValue && commentedFilterItemValue[0]
        });

        this.changesCurrentKey = key;

        const navigationService = getGitNavigationService(this.pageContext);

        if (this.changes) {
            const selectedItem = navigationService.getSelectedItem();
            const previousSelectedPath = selectedItem.value.path;

            this.changes.value = changesCollection;
            this.loadAdornments();

            if (previousSelectedPath !== "") {
                const freshTreeItem = changesCollection.treeItemsByPath[previousSelectedPath];
                const freshFileChange = changesCollection.changesByPath[previousSelectedPath];
                navigationService.setSelectedItem(freshFileChange || (freshTreeItem ? freshTreeItem.data : changesCollection.rootTreeItem.data));
            }
        } else {
            this.changes = new ObservableValue(changesCollection);
            navigationService.setChanges(this.changes);
        }
    };

    private updateLatestChanges = (changes: PullRequestIterationChanges | undefined) => {
        if (changes) {
            this.iterationChangesCache[`${changes.iterationId}-${changes.baseIterationId}`] = changes;
        }

        const changesCollection = this.processChanges(changes);
        if (this.latestChanges) {
            this.latestChanges.value = changesCollection;
        } else {
            this.latestChanges = new ObservableValue(changesCollection);
        }

        this.refreshViewedCount();
    };

    private updateIterations(iterations: GitPullRequestIteration[]): void {
        function isSameCommit(c1: GitCommitRef | undefined, c2: GitCommitRef | undefined) {
            return Boolean(c1 && c2 && c1.commitId === c2.commitId);
        }

        const routingService = getReposRoutingService(this.pageContext);
        this.iterations.value = iterations.map((iteration, index, iterations) =>
            this.convertToIIteration(
                iteration,
                index === 0 || !isSameCommit(iteration.commonRefCommit, iterations[index - 1].commonRefCommit),
                index < iterations.length - 1 && isSameCommit(iteration.commonRefCommit, iterations[index + 1].commonRefCommit),
                routingService
            )
        );

        const previousLatestIterationId = this.latestIterationId;
        this.latestIterationId = (this.iterations.length > 0 && this.iterations.value[this.iterations.length - 1].id) || 0;
        if (previousLatestIterationId > 0 && this.latestIterationId > previousLatestIterationId) {
            getReposMessagesService(this.pageContext).showMessage({
                buttons: [
                    {
                        id: "view-latest",
                        onClick: () => {
                            this.setSelectedTabId("files");
                            this.selectIteration(this.getLatestIterationId(), 0);
                        },
                        text: Resources.ViewLatestChanges
                    }
                ],
                renderMessage: () => <span>{Resources.HasNewIterations}</span>,
                severity: MessageCardSeverity.Info,
                type: PullRequestMessageType.newChanges
            });

            // Update iteration filter if necessary based on the new iteration
            this.updateIterationFilterText();
        }

        // show a banner for Large Pull Requests to allow user invalidate cache manually (for urgent/unexpected cases)
        if (isFeatureFlagEnabled(this.pageContext, "Policy.EventBasedCacheEnabled.ShowRefreshBanner", false) && 
            this.pullRequest.status === PullRequestStatus.Active &&
            this.dataService.getIsPolicyResultsCached()) {
            getReposMessagesService(this.pageContext).showMessage({
                buttons: [
                    {
                        id: "invalidate-cached-policies",
                        onClick: (e) => {
                            this.refreshWithCacheInvalidation();
                            onClickFPS(this.pageContext, window.location.href, false, e);
                        },
                        text: Resources.ViewLatestChanges
                    }
                ],
                renderMessage: () => <span>{Resources.IsCachedPolicyResults}</span>,
                severity: MessageCardSeverity.Warning,
                type: PullRequestMessageType.newChanges
            });
        }

        if (this.selectedIterationId.value === 0 && this.latestIterationId > 0) {
            this.selectedIterationId.value = this.latestIterationId;
        }

        // Local changes will stick to the version at which the user started making edits.
        const latestVersion = this.getVersionsForIterationRange(this.latestIterationId, 0).modifiedVersion;
        if (!this.localChanges) {
            this.localChanges = new LocalFileChangesCollection(this.pageContext, latestVersion);
        } else if (!this.localChanges.isDirty().value) {
            this.localChanges.updateVersion(latestVersion);
        }
    }

    private updateIterationFilterText() {
        const iterationFilterText =
            this.selectedIterationId.value === this.latestIterationId && this.baseIterationId.value === 0
                ? ""
                : this.selectedIterationId.value === this.baseIterationId.value + 1
                ? format(Resources.ViewingOneIteration, this.selectedIterationId.value)
                : format(Resources.ViewingManyIterations, this.baseIterationId.value + 1, this.selectedIterationId.value);

        this.filesFilter.setFilterItemState(FilesFilterKeys.iteration, { value: iterationFilterText });
    }

    private async updateLiveUpdates() {
        const updatesService = await this.getUpdatesService();
        if (this.liveUpdatesEnabled.value) {
            updatesService.enableLiveUpdates();
        } else {
            updatesService.disableLiveUpdates();
        }
    }

    private updateReviewersAndPolicies = (reviewers?: IdentityRefWithVote[]): void => {
        // If reviewers were not passed (i.e. policy updates) then use the existing set of reviewers
        if (!reviewers) {
            reviewers = this.reviewers.value.map(r => r.baseReviewer);
        }

        // Update the reviewer policy lookup
        const reviewerPolicies = getAutomaticReviewerPolicies(this.dataService.getPolicies().value);
        this.reviewerPolicyLookup.value = createReviewerPolicyLookup(reviewerPolicies);

        // Update the set of reviewers
        const voteMap = this.createVotedByForMap(...reviewers);
        this.reviewers.value = reviewers.map(item => this.convertToIReviewer(item, voteMap));
    };

    private updateTags = (tags: WebApiTagDefinition[]): void => {
        this.tagRefs = tags;
        this.tags.value = tags.map(x => x.name);
    };

    private updateTransitionItems(): void {
        this.getWorkItemTransitionsByWitIds(this.linkedWitItems.value.map(item => Number(item.id))).then((items) => {
            this.relatedWitTransitionItems.value = items;
            this.updateTransitionWarningMessage();
        })
    }

    private async getWorkItemTransitionsByWitIds(ids: number[]): Promise<WorkItemNextStateOnTransition[]> {
        if (!ids.length) {
            return [];
        }
        return this.witRestTrackingClient.getWorkItemNextStatesOnCheckinAction(ids);
    }
}

const serviceName = "IPullRequestViewService";

export function getPullRequestViewService(pageContext: IVssPageContext): IPullRequestViewService {
    return pageContext.getService<IPullRequestViewService>(serviceName);
}

Services.add(serviceName, { serviceFactory: PullRequestViewService });
